import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { ScopePicker } from "@/components/admin/ScopePicker";

const blank = { name: "", mobile: "", email: "", designation: "", role: "Manager", login_id: "", password: "", confirm_password: "",
  status: "Active", franchise_partner_id: "", scope: { locations: "all", franchises: "all", dp_ids: [] } };

export function UserFormDialog({ open, user, meta, onClose, onSaved }) {
  const isEdit = !!user;
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(user ? { ...blank, ...user, email: user.email || "", password: "", confirm_password: "", franchise_partner_id: user.franchise_partner_id || "",
      scope: { ...blank.scope, ...(user.scope || {}) } } : blank);
  }, [open, user]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isFo = f.role === "Franchise Owner";
  const isSuper = f.role === "Super Admin";

  const save = async () => {
    setSaving(true);
    const body = { name: f.name, mobile: f.mobile, email: f.email, designation: f.designation, role: f.role, login_id: f.login_id,
      status: f.status, scope: f.scope, franchise_partner_id: isFo ? f.franchise_partner_id : null };
    if (!isEdit) { body.password = f.password; body.confirm_password = f.confirm_password; }
    try {
      let saved;
      if (isEdit) ({ data: saved } = await api.put(`/admin/users/${user.id}`, body)); else ({ data: saved } = await api.post("/admin/users", body));
      toast.success(isEdit ? "User updated" : `User ${f.login_id} created`);
      onSaved(isEdit ? null : saved); onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-head">{isEdit ? `Edit ${user.name}` : "Add New User"}</DialogTitle></DialogHeader>
        <Section title="Personal Details">
          <F l="Full Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="uf-name" /></F>
          <F l="Mobile Number"><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="uf-mobile" /></F>
          <F l="Email Address"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="uf-email" /></F>
          <F l="Designation"><Input value={f.designation} onChange={(e) => set("designation", e.target.value)} data-testid="uf-designation" /></F>
          <F l="Assigned Role *">
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.roles || []).map((r) => <SelectItem key={r.id} value={r.name} data-testid={`uf-role-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F l="Account Status">
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="uf-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Active", "Suspended", "Disabled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </Section>
        <Section title="Login Credentials">
          <F l="Login ID *"><Input value={f.login_id} onChange={(e) => set("login_id", e.target.value)} placeholder="CTM-MGR-001" data-testid="uf-login-id" /></F>
          <div />
          {!isEdit && <>
            <F l="Initial Password *"><Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="uf-password" /></F>
            <F l="Confirm Password *"><Input type="password" autoComplete="new-password" value={f.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} data-testid="uf-confirm-password" /></F>
            <p className="col-span-2 -mt-1 text-[11px] text-muted-foreground">Min 8 characters with letters and numbers. Stored securely hashed — never shown again.</p>
          </>}
          {isEdit && <p className="col-span-2 text-xs text-muted-foreground">Use "Reset Password" from the Actions menu to change this user's password.</p>}
        </Section>
        {isFo && (
          <Section title="Linked Franchise Partner *">
            <div className="col-span-2">
              <Select value={f.franchise_partner_id} onValueChange={(v) => set("franchise_partner_id", v)}>
                <SelectTrigger data-testid="uf-franchise"><SelectValue placeholder="Select the franchise this owner manages" /></SelectTrigger>
                <SelectContent>{(meta?.franchises || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.referral_number})</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">Franchise Owners only ever see data attributed to this franchise.</p>
            </div>
          </Section>
        )}
        {!isFo && !isSuper && <ScopePicker meta={meta} scope={f.scope} onChange={(s) => set("scope", s)} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="uf-save">{saving ? "Saving…" : "Save User"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-2"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p><div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div></div>
);
const F = ({ l, children }) => <div><Label>{l}</Label>{children}</div>;
